import { useEffect, useRef, useState } from 'react';
import type {
  AdminActiveCounts,
  AdminActiveDay,
  AdminActivePlay,
  AdminActiveResponse,
  AdminActiveWindows,
  SessionKind,
} from '@boardflight/shared';
import { api } from '../api.js';
import { Async, Filter, Panel, duration, useAsync } from '../ui.js';

/**
 * Active accounts and Boards — DAU, WAU and MAU (`docs/admin-plan.md` §3.5).
 *
 * Accounts that signed in or refreshed a session, and Boards a desktop app reached. Never "played":
 * no play names an account or a Board, so the page says what it counts rather than letting the
 * three letters imply it. Play has a panel of its own, as sessions and games: how many, how long, and
 * how many games were played. Never per player or per Board, since no play names one.
 *
 * Two clocks, kept apart on the page as they are in the route: the head is the last 24 hours, 7 and
 * 30 days as of now, exact from the first day; the chart and the table are UTC days, from the day
 * recording began, with a window that reaches back before it left blank rather than drawn short.
 */

const KIND_LABELS: Record<SessionKind, string> = {
  web: 'Website',
  device: 'Desktop app',
  plugin: 'CLI and plugins',
};

const KIND_FILTER = [
  { value: '', label: 'Any' },
  { value: 'web', label: KIND_LABELS.web },
  { value: 'device', label: KIND_LABELS.device },
  { value: 'plugin', label: KIND_LABELS.plugin },
] as const;

const RANGE_FILTER = [
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
  { value: '180', label: 'Last 180 days' },
] as const;

interface Series {
  key: string;
  label: string;
  color: string;
}

/** The three windows, in the fixed order their colours are assigned in. */
const WINDOW_SERIES = [
  { key: 'daily', label: 'Daily', color: 'var(--series-1)' },
  { key: 'weekly', label: 'Weekly (7 days)', color: 'var(--series-2)' },
  { key: 'monthly', label: 'Monthly (30 days)', color: 'var(--series-3)' },
] as const satisfies readonly (Series & { key: keyof AdminActiveCounts })[];

const PLAY_SERIES = [
  { key: 'sessions', label: 'Sessions', color: 'var(--series-1)' },
  { key: 'games', label: 'Games played', color: 'var(--series-2)' },
] as const satisfies readonly Series[];

const LENGTH_SERIES = [{ key: 'average', label: 'Average session', color: 'var(--series-1)' }] as const satisfies readonly Series[];

const count = (value: number | null) => (value === null ? '—' : String(value));
/** Minutes to one decimal, which is what the session-length chart is drawn in. */
const minutes = (value: number | null) => (value === null ? '—' : `${value} min`);

export function Active() {
  const [days, setDays] = useState('90');
  const [kind, setKind] = useState('');
  const state = useAsync(
    () => api<AdminActiveResponse>(`/v1/admin/active?days=${days}${kind ? `&kind=${kind}` : ''}`),
    [days, kind],
  );

  return (
    <>
      <Panel
        title="Active"
        actions={
          <div className="filters">
            <Filter label="Accounts from" value={kind} onChange={setKind} options={KIND_FILTER} />
            <Filter label="Range" value={days} onChange={setDays} options={RANGE_FILTER} />
          </div>
        }
      >
        <Async state={state}>
          {(data) => {
            // One client's row when one is picked: a filter on the number, never a second number.
            const accounts = kind ? data.now.byKind.find((row) => row.kind === kind) ?? { day: 0, week: 0, month: 0 } : data.now.accounts;
            return (
              <>
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th />
                        <th className="num">Last 24 hours</th>
                        <th className="num">Last 7 days</th>
                        <th className="num">Last 30 days</th>
                        <th className="num" title="The last 24 hours over the last 30 days: how much of a month's audience comes back on any one day">
                          24 h ÷ 30 days
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <WindowRow label={kind ? `Accounts · ${KIND_LABELS[kind as SessionKind]}` : 'Accounts'} windows={accounts} strong />
                      <WindowRow label="Boards on" windows={data.now.boards} strong />
                    </tbody>
                  </table>
                </div>
                <p className="note">
                  Active is a sign-in or a session refresh, so an app left running counts every day; a Board is on when a
                  desktop app reached it. Never a play. Excluded accounts are left out.
                </p>
              </>
            );
          }}
        </Async>
      </Panel>

      <Panel title="Play">
        <Async state={state}>{(data) => <Play data={data} />}</Async>
      </Panel>

      <Panel title="By day">
        <Async state={state}>
          {(data) =>
            data.since === null ? (
              <p className="muted">Nothing recorded yet. Days are counted from the day this was deployed.</p>
            ) : (
              <ByDay data={data} since={data.since} />
            )
          }
        </Async>
      </Panel>
    </>
  );
}

/**
 * The days held, and only those. A range that reaches back before recording began is cut at the
 * first day held: drawn whole, the first weeks after this shipped were a 90-day axis with one point
 * at its right-hand end, which reads as a broken chart rather than as a new one.
 */
function ByDay({ data, since }: { data: AdminActiveResponse; since: string }) {
  const held = data.days.filter((day) => day.day >= since);
  return (
    <>
      <p className="note">
        UTC days since {since}, kept {data.retentionDays} days. Weekly and monthly fill in after 7 and 30 days of
        records. Today is so far.
      </p>
      {held.length < 2 ? (
        <p className="muted">The chart starts once a second day has been recorded.</p>
      ) : (
        <>
          <Chart
            title={`Accounts${data.kind ? ` · ${KIND_LABELS[data.kind]}` : ''}`}
            days={held}
            series={WINDOW_SERIES}
            pick={(day) => day.accounts}
          />
          <Chart title="Boards on" days={held} series={WINDOW_SERIES} pick={(day) => day.boards} />
        </>
      )}
    </>
  );
}

/**
 * Game sessions, how long they ran and how many games had one, over the same three windows as the
 * head, then by day across the whole range.
 *
 * The days here are not cut at `since`: play has its own rollup, older than the day tables. Sessions
 * are not narrowed by the Accounts filter, since a session names no account, and the panel says so.
 * The median is the length to read: a Board left on with a game open files a session of hours, and
 * pulls the average with it.
 */
function Play({ data }: { data: AdminActiveResponse }) {
  const windows = [data.now.play.day, data.now.play.week, data.now.play.month];
  const row = (label: string, cell: (play: AdminActivePlay) => string, strong?: boolean) => (
    <tr className={strong ? 'strong' : undefined}>
      <td>{label}</td>
      {windows.map((play, index) => (
        <td key={index} className="num">
          {cell(play)}
        </td>
      ))}
    </tr>
  );
  const days = data.days;
  return (
    <>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th />
              <th className="num">Last 24 hours</th>
              <th className="num">Last 7 days</th>
              <th className="num">Last 30 days</th>
            </tr>
          </thead>
          <tbody>
            {row('Game sessions', (play) => String(play.sessions), true)}
            {row('Games played', (play) => String(play.games), true)}
            {row('Sessions per game', (play) => (play.games ? (play.sessions / play.games).toFixed(1) : '—'))}
            {row('Playtime', (play) => (play.sessions ? duration(play.playtimeMs) : '—'))}
            {row('Median session', (play) => duration(play.medianSessionMs))}
            {row('Average session', (play) => (play.sessions ? duration(play.playtimeMs / play.sessions) : '—'))}
          </tbody>
        </table>
      </div>
      <p className="note">
        A session is one game open on a Board until it closes or goes quiet; on a web game, every launch is a new one.
        Plays are not tied to accounts or Boards, so there is no per-player figure, and the Accounts filter does not
        narrow these. Excluded accounts are left out.
      </p>
      {days.length < 2 ? null : (
        <>
          <Chart
            title="Sessions and games, by UTC day"
            days={days}
            series={PLAY_SERIES}
            pick={(day) => ({ sessions: day.sessions, games: day.games })}
          />
          <Chart
            title="Average session, minutes"
            days={days}
            series={LENGTH_SERIES}
            format={minutes}
            pick={(day) => ({
              average: day.sessions ? Math.round(day.playtimeMs / day.sessions / 6000) / 10 : null,
            })}
          />
        </>
      )}
    </>
  );
}

function WindowRow({ label, windows, strong, indent }: {
  label: string;
  windows: AdminActiveWindows;
  strong?: boolean;
  indent?: boolean;
}) {
  return (
    <tr className={strong ? 'strong' : undefined}>
      <td className={indent ? 'muted indent' : undefined}>{label}</td>
      <td className="num">{windows.day}</td>
      <td className="num">{windows.week}</td>
      <td className="num">{windows.month}</td>
      <td className="num muted">{windows.month ? `${Math.round((windows.day / windows.month) * 100)}%` : '—'}</td>
    </tr>
  );
}

/** The panel's inner width, followed as the window resizes, so the chart's text stays its own size. */
export function useWidth(): [React.RefObject<HTMLDivElement | null>, number] {
  const ref = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const observer = new ResizeObserver(([entry]) => setWidth(entry?.contentRect.width ?? 0));
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  return [ref, width];
}

/** 0, then a round step: 1, 2 or 5 times a power of ten, so the gridlines land on clean numbers. */
export function niceTicks(max: number): number[] {
  if (max <= 0) return [0, 1];
  const rough = max / 4;
  const power = 10 ** Math.floor(Math.log10(rough));
  const step = [1, 2, 5, 10].map((m) => m * power).find((s) => s >= rough) ?? power * 10;
  const ticks: number[] = [];
  for (let value = 0; value < max + step; value += Math.max(1, step)) ticks.push(value);
  return ticks;
}

export const HEIGHT = 200;
export const PAD = { top: 12, right: 64, bottom: 24, left: 36 };

/**
 * A few lines on one axis, one per series: daily, weekly and monthly for the counts of people, which
 * are one measure at three windows and so one scale; or the play lines, which share a unit too.
 *
 * A gap where a window reaches back before recording began, rather than a line drawn to zero. A
 * crosshair and a tooltip on hover, a legend, and each line's latest value at its end. The table of
 * the same numbers that used to sit under the charts is gone (owner, 2026-10-28): the tooltip reads
 * any day.
 */
function Chart({ title, days, series: SERIES, pick, format = count }: {
  title: string;
  days: AdminActiveDay[];
  series: readonly Series[];
  pick: (day: AdminActiveDay) => Record<string, number | null>;
  format?: (value: number | null) => string;
}) {
  const [ref, width] = useWidth();
  const [hover, setHover] = useState<number | null>(null);

  const values = days.map(pick);
  const max = Math.max(1, ...values.flatMap((v) => SERIES.map((series) => v[series.key] ?? 0)));
  const ticks = niceTicks(max);
  const top = ticks[ticks.length - 1] ?? 1;
  const plotWidth = Math.max(0, width - PAD.left - PAD.right);
  const plotHeight = HEIGHT - PAD.top - PAD.bottom;
  const x = (index: number) => PAD.left + (days.length <= 1 ? plotWidth / 2 : (index / (days.length - 1)) * plotWidth);
  const y = (value: number) => PAD.top + plotHeight - (value / top) * plotHeight;

  /** One path per unbroken run of values; a null ends a run. */
  function path(key: string): string {
    let d = '';
    let open = false;
    values.forEach((v, index) => {
      const value = v[key] ?? null;
      if (value === null) {
        open = false;
        return;
      }
      d += `${open ? 'L' : 'M'}${x(index).toFixed(1)},${y(value).toFixed(1)}`;
      open = true;
    });
    return d;
  }

  // End labels, in text ink beside a dot of the line's colour, pushed apart when two would touch.
  const last = values.length - 1;
  const ends = SERIES.flatMap((series) => {
    const value = values[last]?.[series.key];
    return value === null || value === undefined ? [] : [{ ...series, value, at: y(value) }];
  }).sort((a, b) => a.at - b.at);
  for (let i = 1; i < ends.length; i++) {
    const above = ends[i - 1]!;
    if (ends[i]!.at - above.at < 13) ends[i]!.at = above.at + 13;
  }

  // Which day the pointer is over: the nearest point, so the whole height is a hit target.
  function onMove(event: React.MouseEvent<SVGRectElement>) {
    const box = event.currentTarget.getBoundingClientRect();
    const fraction = (event.clientX - box.left) / Math.max(1, box.width);
    setHover(Math.min(last, Math.max(0, Math.round(fraction * last))));
  }

  // A label every so many days, never crowding: about one per 90px.
  const every = Math.max(1, Math.ceil(days.length / Math.max(1, Math.floor(plotWidth / 90))));
  const hovered = hover === null ? null : { day: days[hover]!, value: values[hover]! };

  return (
    <div className="chart">
      <div className="chart-head">
        <span className="chart-title">{title}</span>
        <span className="chart-legend">
          {SERIES.map((series) => (
            <span key={series.key} className="chart-key">
              <span className="chart-swatch" style={{ background: series.color }} />
              {series.label}
            </span>
          ))}
        </span>
      </div>
      <div className="chart-plot" ref={ref}>
        {width > 0 ? (
          <svg
            width={width}
            height={HEIGHT}
            role="img"
            aria-label={`${title}: ${SERIES.map((series) => series.label.toLowerCase()).join(', ')}, by day`}
          >
            {ticks.map((tick) => (
              <g key={tick}>
                <line className="chart-grid" x1={PAD.left} x2={PAD.left + plotWidth} y1={y(tick)} y2={y(tick)} />
                <text className="chart-axis" x={PAD.left - 6} y={y(tick)} dy="0.32em" textAnchor="end">
                  {tick}
                </text>
              </g>
            ))}
            {days.map((day, index) =>
              index % every === 0 || index === last ? (
                index !== last && last - index < every / 2 ? null : (
                  <text
                    key={day.day}
                    className="chart-axis"
                    x={x(index)}
                    y={HEIGHT - 6}
                    textAnchor={index === 0 ? 'start' : index === last ? 'end' : 'middle'}
                  >
                    {index === last ? 'today' : day.day.slice(5)}
                  </text>
                )
              ) : null,
            )}
            {SERIES.map((series) => (
              <path key={series.key} d={path(series.key)} fill="none" stroke={series.color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
            ))}
            {ends.map((end) => (
              <g key={end.key}>
                <circle cx={x(last)} cy={y(end.value)} r={4} fill={end.color} stroke="var(--panel)" strokeWidth={2} />
                <text className="chart-end" x={x(last) + 10} y={end.at} dy="0.32em">
                  {format(end.value)}
                </text>
              </g>
            ))}
            {hovered !== null && hover !== null ? (
              <g>
                <line className="chart-cross" x1={x(hover)} x2={x(hover)} y1={PAD.top} y2={PAD.top + plotHeight} />
                {SERIES.map((series) => {
                  const value = hovered.value[series.key] ?? null;
                  return value === null ? null : (
                    <circle key={series.key} cx={x(hover)} cy={y(value)} r={4} fill={series.color} stroke="var(--panel)" strokeWidth={2} />
                  );
                })}
              </g>
            ) : null}
            <rect
              x={PAD.left}
              y={PAD.top}
              width={plotWidth}
              height={plotHeight}
              fill="transparent"
              onMouseMove={onMove}
              onMouseLeave={() => setHover(null)}
            />
          </svg>
        ) : (
          <div style={{ height: HEIGHT }} />
        )}
        {hovered !== null && hover !== null ? (
          <div
            className="chart-tip"
            style={{
              left: Math.min(x(hover) + 12, Math.max(0, width - 190)),
              top: PAD.top + 28,
            }}
          >
            <div className="mono">
              {hovered.day.day}
              {hover === last ? ' · so far' : ''}
            </div>
            {SERIES.map((series) => (
              <div key={series.key} className="chart-tip-row">
                <span className="chart-swatch" style={{ background: series.color }} />
                <span className="grow">{series.label}</span>
                <span className="num">{format(hovered.value[series.key] ?? null)}</span>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}
